import { Accordion, Button, ButtonsGroup, Card, Grid, Heading, Icon, InputPhone, InputText, PageHeader, PageWrapper, Select, Typo } from "#/atoms";
import { DESKTOP_SPACING, FONT_SIZE, NATURAL } from "@/theme";
import { FC, memo, useCallback, useState } from "react";
import { HeadingIcon, RadioButtonGroup } from "@/components/molecules";
import { SmsTemplate, WhatsappTemplate } from "@/types";
import { StyledCustomer, StyledCustomerName } from "../Order/CustomerAndPackage/CustomerAndPackage.styled";
import { useInput, useSelect } from "@/hooks";

import { MessagingProps } from "./Messaging.interface";
import { RadioButtonProps } from "@/components/molecules/RadioButtonGroup/RadioButtonGroup.interface";
import { Textarea } from "@/components/atoms/Form/Textarea/Textarea.stories";

const Settings: FC<MessagingProps> = ({ 
  onAddSmsTemplate,
  onEditSmsTemplate,
  onDeleteSmsTemplate,
  onReloadSmsTemplates,
  onAddWhatsappTemplate,
  onEditWhatsappTemplate,
  onDeleteWhatsappTemplate,
  onReloadWhatsappTemplates,
  onFindCustomer,
  onRemoveCustomer,
  onSendWhatsappMessage,
  selectedCustomer,
  smsTemplates,
  whatsappTemplates,
  accordionState = 'open',
    }) => {

  const messageText = useInput('');
  const messageTargetType = useInput('');
  const phoneNumber = useInput('');

  const smsTemplateSelect = useSelect('');
  const smsTemplateSubject = useInput('');
  const smsTemplateMessageText = useInput('');

  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState<string | null>(null);

  const whatsappTemplateSelect = useSelect('');
  const whatsappTemplateSubject = useInput('');
  const whatsappTemplateMessageText = useInput('');

  const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState<string | null>(null);

const messageTargetTypes : RadioButtonProps[] = [{
      id: "1",
      label: "By Customer",
      value: "1"
    },
    {
      id: "2",
      label: "By Number",
      value: "2"
    }
  ]

  const finalSmsTemplates = [{
    id: '',
    label: '',
    text: ''
  }].concat(
  smsTemplates.map((s) => ({
    id: s.id,
    label: s.subject,
    text: s.text
  }))).concat([{
    id: 'new',
    label: '---New---',
    text: ''
  }]);

  const finalWhatsappTemplates = [{
    id: '',
    label: '',
    text: ''
  }].concat(
  whatsappTemplates.map((s) => ({
    id: s.id,
    label: s.subject,
    text: s.text
  }))).concat([{
    id: 'new',
    label: '---New---',
    text: ''
  }]);
  
  const handlemessageTargetTypeChange = useCallback((groupName: string, selectedValue: string) => {
    messageTargetType.setValue(selectedValue);
  }, []);

  const handleMessageSend = () => {
    onSendWhatsappMessage(phoneNumber.value, messageText.value)
  }

  const handleSmsTemplateSelected = (e) => {
    let smsTemplate: SmsTemplate | undefined;
    
    switch(e.target.value){
      case '':
        setSelectedSmsTemplateId(null);
        smsTemplateSubject.setValue('');
        smsTemplateMessageText.setValue('');
        break;
      case 'new':
        setSelectedSmsTemplateId('new');
        smsTemplateSubject.setValue('');
        smsTemplateMessageText.setValue('');
        break;
      default:
        smsTemplate = smsTemplates.find(s => s.id === e.target.value);
        smsTemplateSubject.setValue(smsTemplate?.subject ?? '');
        smsTemplateMessageText.setValue(smsTemplate?.text ?? '');
        setSelectedSmsTemplateId(smsTemplate?.id ?? '');
      break;
    }

  }
  
const handleWhatsappTemplateSelected = (e) => {
    let whatsappTemplate: WhatsappTemplate | undefined;
    
    switch(e.target.value){
      case '':
        setSelectedWhatsappTemplateId(null);
        whatsappTemplateSubject.setValue('');
        whatsappTemplateMessageText.setValue('');
        break;
      case 'new':
        setSelectedWhatsappTemplateId('new');
        whatsappTemplateSubject.setValue('');
        whatsappTemplateMessageText.setValue('');
        break;
      default:
        whatsappTemplate = whatsappTemplates.find(s => s.id === e.target.value);
        whatsappTemplateSubject.setValue(whatsappTemplate?.subject ?? '');
        whatsappTemplateMessageText.setValue(whatsappTemplate?.text ?? '');
        setSelectedWhatsappTemplateId(whatsappTemplate?.id ?? '');
      break;
    }

  }

  const handleSmsTemplateSave = useCallback(async () => {
    const smsTemplate = {
      id: selectedSmsTemplateId ?? '',
      subject: smsTemplateSubject.value,
      text: smsTemplateMessageText.value
    };
    
    if (selectedSmsTemplateId === 'new') {
      await onAddSmsTemplate(smsTemplate);
    } else if (selectedSmsTemplateId) {
      await onEditSmsTemplate(smsTemplate);
    }
      onReloadSmsTemplates();
  }, [smsTemplateSelect, smsTemplateSubject]);

  const handleWhatsappTemplateSave = useCallback(async () => {
    const whatsappTemplate = {
      id: selectedWhatsappTemplateId ?? '',
      subject: whatsappTemplateSubject.value,
      text: whatsappTemplateMessageText.value
    };
    
    if (selectedWhatsappTemplateId === 'new') {
      await onAddWhatsappTemplate(whatsappTemplate);
    } else if (selectedWhatsappTemplateId) {
      await onEditWhatsappTemplate(whatsappTemplate);
    }
      onReloadWhatsappTemplates();
  }, [whatsappTemplateSelect, whatsappTemplateSubject]);

  const handleSmsTemplateDelete = useCallback(async () => {
    if (selectedSmsTemplateId) {
      await onDeleteSmsTemplate(selectedSmsTemplateId);
      onReloadSmsTemplates();
      smsTemplateSubject.setValue('');
      smsTemplateMessageText.setValue('');
    }
  }, [smsTemplateSelect, smsTemplateSubject]);

  const handleWhatsappTemplateDelete = useCallback(async () => {
    if (selectedWhatsappTemplateId) {
      await onDeleteWhatsappTemplate(selectedWhatsappTemplateId);
      onReloadWhatsappTemplates();
      whatsappTemplateSubject.setValue('');
      whatsappTemplateMessageText.setValue('');
    }
  }, [whatsappTemplateSelect, whatsappTemplateSubject]);

  return (
    <PageWrapper>
      <PageHeader>
        <Heading scale="h1" fontColor={NATURAL.K}>Messaging</Heading>
        <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Send messages and manage templates</Typo>
      </PageHeader>

      <Card>
        <Accordion
                header={<HeadingIcon icon="sms" title="Send Whatsapp Messages" />}
                showArrow
                state={accordionState}
              >
                <br/>
                <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Custom messages only available to customers who have opted in to messaging</Typo>
                <br/>
            <RadioButtonGroup groupName="messageTargetTypes" inputs={messageTargetTypes} selectedValue={messageTargetType.value} onChangeValue={handlemessageTargetTypeChange} />
<br/>
            {messageTargetType.value === '1' && <>
            {selectedCustomer ? (
                            <StyledCustomer key={selectedCustomer.id}>
                              <StyledCustomerName>Customer: <strong>{selectedCustomer.firstName} {selectedCustomer.lastName}</strong></StyledCustomerName>
                              <Button actionType="icon" onClick={onRemoveCustomer}><Icon type="cross" /></Button>
                            </StyledCustomer>
                        ) : (
                          <p>No customer have been added yet.</p>
                        )}
                        <ButtonsGroup fullWidth>
                          <Button onClick={onFindCustomer}>Find Customer</Button>
                        </ButtonsGroup>
            </>}

            {messageTargetType.value === '2' && <InputPhone type="tel" label="Number" value={phoneNumber.value} onChange={phoneNumber.onChange} /> }
            <br/>
            <Textarea label="Message text" value={messageText.value} onChange={messageText.onChange} />
        <ButtonsGroup fullWidth>
          <Button onClick={handleMessageSend} state={ messageText.value ? 'default' : 'disabled' } >Send</Button>
        </ButtonsGroup>
        </Accordion>
      </Card>

      <Card>
         <Accordion
                header={<HeadingIcon icon="sms" title="SMS Templates" />}
                showArrow
                state={accordionState}
              >
        <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(1, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
          <Select label="Choose" options={finalSmsTemplates} onChange={handleSmsTemplateSelected} />
          <InputText type="text" label="Subject" value={smsTemplateSubject.value} onChange={smsTemplateSubject.onChange} />
          <Textarea label="Message text" value={smsTemplateMessageText.value} onChange={smsTemplateMessageText.onChange} />
        </Grid>
        <ButtonsGroup fullWidth>
          <Button onClick={handleSmsTemplateSave} state={ selectedSmsTemplateId ? 'default' : 'disabled' } >Save</Button>
          <Button onClick={handleSmsTemplateDelete} state={ selectedSmsTemplateId ? 'default' : 'disabled' } >Delete</Button>
        </ButtonsGroup>
        </Accordion>
      </Card>

      <Card>
        <Accordion
                header={<HeadingIcon icon="whatsapp" title="Whatsapp Templates" />}
                showArrow
                state={accordionState}
              >
            <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(1, 1fr)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
            <Select label="Choose" options={finalWhatsappTemplates} onChange={handleWhatsappTemplateSelected} />
            <InputText type="text" label="Subject" value={whatsappTemplateSubject.value} onChange={whatsappTemplateSubject.onChange} />
            <Textarea label="Message text" value={whatsappTemplateMessageText.value} onChange={whatsappTemplateMessageText.onChange} />
            </Grid>
            <ButtonsGroup fullWidth>
            <Button onClick={handleWhatsappTemplateSave} state={ selectedWhatsappTemplateId ? 'default' : 'disabled' } >Save</Button>
            <Button onClick={handleWhatsappTemplateDelete} state={ selectedWhatsappTemplateId ? 'default' : 'disabled' } >Delete</Button>
            </ButtonsGroup>
        </Accordion>
      </Card>
      
    </PageWrapper>
  );
}

export default memo(Settings);